<template>
    <ion-page>
      <ion-header>
          <ion-toolbar class="professional-header">
            <ion-title>{{ group ? group.name : 'Group Results' }}</ion-title>
            <ion-buttons slot="start" @click="goBack()">
              <div class="back-button">
                <ion-icon :icon="arrowBackOutline" size="large" />
              </div>
            </ion-buttons>
          </ion-toolbar>
      </ion-header>
      <ion-content class="professional-content">
        <!-- Main Results Card -->
        <div class="results-container" v-if="group">
          <!-- Compact Event Header -->
                    <div class="event-header-card">
            <div class="header-main">
              <div class="title-section">
                <h2 class="event-title">{{ group.name }}</h2>
                <div class="header-actions">
                  <span class="status-badge" :class="getEventStatusClass()">
                    {{ getEventStatus() }}
                  </span>
                  <button class="details-toggle-btn" @click="showDetailsPanel = !showDetailsPanel">
                    <ion-icon :icon="informationCircleOutline" />
                    {{ showDetailsPanel ? 'Hide Details' : 'Show Details' }}
                  </button>
                </div>
              </div>

              <div class="event-meta">
                <div class="meta-item" v-if="group.dateTime">
                  <ion-icon :icon="calendarOutline" />
                  <span>{{ formatDateTime(group.dateTime) }}</span>
                </div>
                <div class="meta-item" v-if="group.venue">
                  <ion-icon :icon="locationOutline" />
                  <span>{{ group.venue.name }}</span>
                </div>
                <div class="meta-item" v-if="group.type">
                  <ion-icon :icon="getSportIcon(group.type)" />
                  <span>{{ group.type }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Pool Filter (for sports with pools) -->
          <div class="pool-filter" v-if="hasPools">
            <h4>Filter by Pool</h4>
            <div class="pool-buttons">
              <ion-chip
                :color="selectedPool === null ? 'primary' : 'medium'"
                outline
                @click="selectedPool = null"
                class="pool-chip"
              >
                All Pools
              </ion-chip>
              <ion-chip
                v-for="pool in availablePools"
                :key="pool.id"
                :color="selectedPool && selectedPool.id === pool.id ? 'primary' : 'medium'"
                outline
                @click="selectedPool = pool"
                class="pool-chip"
              >
                {{ pool.name }}
              </ion-chip>
            </div>
          </div>

          <!-- Event Details Panel -->
          <div class="details-panel" v-if="showDetailsPanel">
            <div class="details-header">
              <h3>Event Details</h3>
              <button class="close-details" @click="showDetailsPanel = false">
                <ion-icon :icon="closeOutline" />
              </button>
            </div>

            <div class="details-content">
              <div class="detail-sections">
                <!-- Athletics Track Events -->
                <template v-if="isAthleticsTrack">
                  <div class="detail-section">
                    <h4>Event Configuration</h4>
                    <div class="athletics-config">
                      <div class="config-highlight">
                        <div class="config-item main">
                          <label>Distance</label>
                          <span class="value-large">{{ group.athleticsTrackConfig?.distance || 'N/A' }}</span>
                        </div>
                        <div class="config-item">
                          <label>Event Type</label>
                          <span>{{ group.athleticsTrackConfig?.eventType || group.eventType || 'N/A' }}</span>
                        </div>
                        <div class="config-item">
                          <label>Scoring</label>
                          <span>{{ group.athleticsTrackConfig?.scoring || 'Fastest time wins' }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="detail-section">
                    <h4>Track Setup</h4>
                    <div class="track-setup">
                      <div class="setup-item">
                        <div class="setup-icon">🏃‍♂️</div>
                        <div class="setup-info">
                          <span class="setup-value">{{ group.numberOfHeats || group.athleticsTrackConfig?.numberOfHeats || 1 }}</span>
                          <span class="setup-label">HEAT{{ (group.numberOfHeats || 1) > 1 ? 'S' : '' }}</span>
                        </div>
                      </div>
                      <div class="setup-item">
                        <div class="setup-icon">🛤️</div>
                        <div class="setup-info">
                          <span class="setup-value">{{ group.lanesPerHeat || group.athleticsTrackConfig?.lanesPerHeat || 8 }}</span>
                          <span class="setup-label">LANES PER HEAT</span>
                        </div>
                      </div>
                      <div class="setup-item">
                        <div class="setup-icon">👥</div>
                        <div class="setup-info">
                          <span class="setup-value">{{ group.participants?.length || 0 }}</span>
                          <span class="setup-label">ATHLETES</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="detail-section">
                    <h4>Event Categories</h4>
                    <div class="detail-grid">
                      <div class="detail-item">
                        <label>Age Groups</label>
                        <span>{{ getAgeGroups() }}</span>
                      </div>
                      <div class="detail-item">
                        <label>Gender Categories</label>
                        <span>{{ getGenderCategories() }}</span>
                      </div>
                      <div class="detail-item">
                        <label>Event Type</label>
                        <span>{{ group.eventType || group.athleticsTrackConfig?.eventType || 'Track Event' }}</span>
                      </div>
                      <div class="detail-item">
                        <label>Measurement Type</label>
                        <span>{{ group.athleticsTrackConfig?.measurement || 'Time-based' }}</span>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Golf Events -->
                <template v-if="isGolf">
                  <div class="detail-section">
                    <h4>Golf Tournament</h4>
                    <div class="golf-config">
                      <div class="config-highlight">
                        <div class="config-item main">
                          <label>Format</label>
                          <span class="value-large">{{ group.golfConfig?.format || 'Stroke Play' }}</span>
                        </div>
                        <div class="config-item">
                          <label>Rounds</label>
                          <span>{{ group.rounds || group.golfConfig?.rounds || 'N/A' }}</span>
                        </div>
                        <div class="config-item">
                          <label>Course Par</label>
                          <span>{{ group.parInfo || group.golfConfig?.par || 'N/A' }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Team Sports -->
                <template v-if="isTeamSport">
                  <div class="detail-section">
                    <h4>Match Configuration</h4>
                    <div class="team-config">
                      <div class="config-highlight">
                        <div class="config-item main">
                          <label>Competition Type</label>
                          <span class="value-large">{{ group.competitionType || 'Team H2H' }}</span>
                        </div>
                        <div class="config-item">
                          <label>Teams</label>
                          <span>{{ group.teams?.length || 'N/A' }}</span>
                        </div>
                        <div class="config-item">
                          <label>Matches</label>
                          <span>{{ group.matches?.length || 0 }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Racquet Sports (Tennis/Badminton) -->
                <template v-if="isRacquetSport">
                  <div class="detail-section">
                    <h4>Match Format</h4>
                    <div class="racquet-config">
                      <div class="config-highlight">
                        <div class="config-item main">
                          <label>Format</label>
                          <span class="value-large">{{ group.singlesDoubles || 'Singles' }}</span>
                        </div>
                        <div class="config-item">
                          <label>Sets</label>
                          <span>{{ group.numberOfSets || 3 }}</span>
                        </div>
                        <div class="config-item">
                          <label>Scoring</label>
                          <span>{{ group.scoringType === 'sets' ? 'Best of Sets' : 'Standard' }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Boxing -->
                <template v-if="isBoxing">
                  <div class="detail-section">
                    <h4>Fight Configuration</h4>
                    <div class="boxing-config">
                      <div class="config-highlight">
                        <div class="config-item main">
                          <label>Fight Type</label>
                          <span class="value-large">{{ group.boxingConfig?.fightType || 'Professional' }}</span>
                        </div>
                        <div class="config-item">
                          <label>Rounds</label>
                          <span>{{ group.boxingConfig?.rounds || 10 }}</span>
                        </div>
                        <div class="config-item">
                          <label>Scoring Method</label>
                          <span>{{ group.boxingConfig?.scoringMethod || '10-Point Must' }}</span>
                        </div>
                        <div class="config-item">
                          <label>Round Duration</label>
                          <span>{{ group.boxingConfig?.roundDuration || 3 }} minutes</span>
                        </div>
                        <div class="config-item">
                          <label>Weight Class</label>
                          <span>{{ group.boxingConfig?.weightClass || 'Open' }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Archery -->
                <template v-if="isArchery">
                  <div class="detail-section">
                    <h4>Archery Configuration</h4>
                    <div class="archery-config">
                      <div class="config-highlight">
                        <div class="config-item main">
                          <label>Distance</label>
                          <span class="value-large">{{ group.archeryConfig?.distance || '30' }}m</span>
                        </div>
                        <div class="config-item">
                          <label>Target Face</label>
                          <span>{{ group.archeryConfig?.targetFace || '60cm-6ring' }}</span>
                        </div>
                        <div class="config-item">
                          <label>Arrows/End</label>
                          <span>{{ group.archeryConfig?.arrowsPerEnd || 3 }}</span>
                        </div>
                        <div class="config-item">
                          <label>Ends</label>
                          <span>{{ group.archeryConfig?.numberOfEnds || 12 }}</span>
                        </div>
                        <div class="config-item">
                          <label>Bow Type</label>
                          <span>{{ group.archeryConfig?.bowType || 'Recurve' }}</span>
                        </div>
                        <div class="config-item">
                          <label>Format</label>
                          <span>{{ group.archeryConfig?.competitionFormat || 'Field' }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Event Details (Common) -->
                <div class="detail-section">
                  <h4>Event Details</h4>
                  <div class="event-details">
                    <div class="detail-row">
                      <label>Status</label>
                      <span class="status-badge" :class="group.status">{{ group.status }}</span>
                    </div>
                    <div class="detail-row" v-if="group.venue">
                      <label>Venue</label>
                      <span>{{ group.venue.name }}</span>
                    </div>
                    <div class="detail-row" v-if="group.venue?.address?.formatted">
                      <label>Location</label>
                      <span>{{ group.venue.address.formatted }}</span>
                    </div>
                    <div class="detail-row" v-if="group.dateTime">
                      <label>Date & Time</label>
                      <span>{{ formatDateTime(group.dateTime) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Participants/Teams Summary -->
                <div class="detail-section">
                  <h4>{{ isTeamSport ? 'Teams' : 'Participants' }} ({{ getParticipantCount() }})</h4>
                  <div class="participants-summary">
                    <div class="participant-quick" v-for="participant in getDisplayParticipants()" :key="participant.id">
                      <span class="participant-number">{{ participant.number }}</span>
                      <span class="participant-name">{{ participant.name }}</span>
                      <span class="participant-meta" v-if="participant.member?.profile?.nationality">
                        {{ participant.member.profile.nationality }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>



          <!-- Results Content -->
          <div class="results-content">
            <!-- Individual Sports Results -->
            <template v-if="shouldShowIndividualResults">
              <!-- Time-based, Measurement, and Points Scoring -->
              <IndividualSportsResults
                :participants="filteredParticipants"
                :matches="filteredMatches"
                :scoringType="group.scoringType"
                :sportType="group.type"
                v-if="['time', 'measurement', 'points'].includes(group.scoringType) && group.type !== 'Golf' && !isArchery"
              />

              <!-- Golf Scoring -->
              <GolfResults
                :participants="filteredParticipants"
                :group="group"
                v-if="group.type === 'Golf' || group.scoringType === 'stroke'"
              />

              <!-- Archery Scoring -->
              <ArcheryResults
                :participants="filteredParticipants"
                v-if="isArchery"
              />
            </template>

            <!-- Head-to-Head Sports Results -->
            <template v-if="shouldShowMatchResults">
              <!-- Boxing Results -->
              <BoxingResults
                :matches="filteredMatches"
                :group="group"
                v-if="isBoxing"
              />

              <!-- Tennis/Badminton Results -->
              <RacquetSportsResults
                :matches="filteredMatches"
                v-if="isRacquetSport && !isBoxing"
              />

              <!-- Custom Head-to-Head Results -->
              <CustomHeadToHeadResults
                v-if="isGenericMatchSport && !isBoxing && !isRacquetSport"
                :matches="filteredMatches"
                :getMatchParticipantName="getMatchParticipantName"
                :scoringType="group.scoringType"
              />
            </template>
          </div>
        </div>
      </ion-content>
    </ion-page>
  </template>

<script>
import { defineComponent } from 'vue';
import { IonContent, IonChip } from '@ionic/vue';
import { arrowBackOutline, calendarOutline, locationOutline, timeOutline, trophyOutline, closeOutline, informationCircleOutline } from 'ionicons/icons';
import { golfOutline, bicycleOutline, tennisballOutline, peopleOutline, ribbonOutline } from 'ionicons/icons';
import moment from 'moment';
import RacquetSportsResults from '@/components/RacquetSportsResults.vue';
import IndividualSportsResults from '@/components/IndividualSportsResults.vue';
import GolfResults from '@/components/GolfResults.vue';
import BoxingResults from '@/components/BoxingResults.vue';
import ArcheryResults from '@/components/ArcheryResults.vue';
import CustomHeadToHeadResults from '@/components/CustomHeadToHeadResults.vue';

export default defineComponent({
  name: 'GroupResultsDisplay',
  components: {
    IonContent,
    IonChip,
    RacquetSportsResults,
    IndividualSportsResults,
    GolfResults,
    BoxingResults,
    ArcheryResults,
    CustomHeadToHeadResults,
  },
  data() {
    return {
      group: null,
      showDetailsPanel: false,
      selectedPool: null,
      arrowBackOutline,
      calendarOutline,
      locationOutline,
      timeOutline,
      trophyOutline,
      closeOutline,
      informationCircleOutline,
      golfOutline,
      bicycleOutline,
      tennisballOutline,
      peopleOutline,
      ribbonOutline,
    }
  },
  async mounted() {
    await this.getGroup();
  },
  computed: {
    sortedParticipants() {
      if (!this.group?.participants) return [];
      return [...this.group.participants].sort((a, b) => {
        if (a.position && b.position) {
          return a.position - b.position;
        }
        return 0;
      });
    },
    isAthleticsTrack() {
      return this.group?.type === 'Athletics (Track)' ||
             this.group?.type === 'Athletics' ||
             this.group?.type === 'Race' ||
             this.group?.athleticsTrackConfig;
    },
    isGolf() {
      return this.group?.type === 'Golf' || this.group?.scoringType === 'stroke';
    },
    isTeamSport() {
      return this.group?.type === 'Team H2H' ||
             this.group?.type === 'Soccer' ||
             this.group?.type === 'Basketball' ||
             this.group?.competitionType === 'team' ||
             (this.group?.matches && this.group.matches.length > 0);
    },
    isRacquetSport() {
      if (this.group?.type === 'Custom Head-to-Head Sport') {
        return false;
      }
      return this.group?.type === 'Tennis' ||
             this.group?.type === 'Badminton' ||
             this.group?.type === 'Table Tennis' ||
             this.group?.type === 'Squash' ||
             this.group?.scoringType === 'sets';
    },

    isBoxing() {
      return this.group?.type === 'Boxing' ||
             this.group?.scoringType === 'rounds' ||
             this.group?.boxingConfig;
    },

    isArchery() {
      return this.group?.type === 'Archery' ||
             this.group?.scoringType === 'archery' ||
             this.group?.archeryConfig;
    },

    isGenericMatchSport() {
      return (this.group?.scoringType === 'result' || this.group?.type === 'Custom Head-to-Head Sport');
    },
    shouldShowIndividualResults() {
      const hasParticipants = this.group?.participants && this.group.participants.length > 0;
      const hasNoMatches = !this.group?.matches || this.group.matches.length === 0;
      const isCustomHeadToHead = this.group?.type === 'Custom Head-to-Head Sport';
      const hasMatches = this.group?.matches && this.group.matches.length > 0;

      console.log('Debug Individual Results:', {
        hasParticipants,
        hasNoMatches,
        isCustomHeadToHead,
        hasMatches,
        participantCount: this.group?.participants?.length,
        matchCount: this.group?.matches?.length,
        scoringType: this.group?.scoringType,
        isIndividualSport: this.group?.isIndividualSport
      });

      // Show individual results if:
      // 1. Has participants and no matches (traditional individual sport)
      return (hasParticipants && hasNoMatches);
    },
    shouldShowMatchResults() {
      const hasMatches = this.group?.matches && this.group.matches.length > 0;
      console.log('Debug Match Results:', {
        hasMatches,
        matchCount: this.group?.matches?.length,
        scoringType: this.group?.scoringType
      });
      return hasMatches;
    },

    hasPools() {
      // Check if the sport has pools/groups
      return (this.group?.pools && this.group.pools.length > 0) ||
             (this.group?.matches && this.group.matches.some(match => match.poolId)) ||
             (this.group?.participants && this.group.participants.some(p => p.poolId)) ||
             this.group?.hasPools;
    },

    availablePools() {
      if (!this.hasPools) return [];

      // Get pools from group.pools if available
      if (this.group.pools && this.group.pools.length > 0) {
        return this.group.pools.map(pool => ({
          id: pool.id || pool._id || pool.name,
          name: pool.name || pool.id || 'Pool'
        }));
      }

      // Extract pools from matches
      if (this.group.matches) {
        const poolIds = [...new Set(this.group.matches
          .filter(match => match.poolId)
          .map(match => match.poolId))];
        return poolIds.map(poolId => ({
          id: poolId,
          name: `Pool ${poolId}`
        }));
      }

      // Extract pools from participants
      if (this.group.participants) {
        const poolIds = [...new Set(this.group.participants
          .filter(p => p.poolId)
          .map(p => p.poolId))];
        return poolIds.map(poolId => ({
          id: poolId,
          name: `Pool ${poolId}`
        }));
      }

      return [];
    },

    filteredMatches() {
      if (!this.group?.matches) return [];
      if (!this.selectedPool) return this.group.matches;

      return this.group.matches.filter(match => match.poolId === this.selectedPool.id);
    },

    filteredParticipants() {
      if (!this.group?.participants) return [];

      let participants = this.selectedPool
        ? this.group.participants.filter(participant => participant.poolId === this.selectedPool.id)
        : this.group.participants;

      return [...participants].sort((a, b) => {
        const numA = a.number || 0;
        const numB = b.number || 0;
        return numA - numB;
      });
    }
  },
  methods: {
    getPositionClass(position) {
      if (position === 1) return 'gold';
      if (position === 2) return 'silver';
      if (position === 3) return 'bronze';
      return 'default';
    },
    getEventStatus() {
      // Use the groupMatchStatus field from the API if available
      if (this.group?.groupMatchStatus) {
        return this.group.groupMatchStatus;
      }

      // Fallback to calculated status if groupMatchStatus is not available
      if (!this.group?.participants || this.group.participants.length === 0) {
        return 'Scheduled';
      }

      // Check various result fields that could indicate completion
      const hasResults = (p) => {
        return p.time || p.measurement || p.points || p.strokes ||
               p.distance || p.result || p.score ||
               (p.results && Object.keys(p.results).length > 0);
      };

      if (this.group.participants.every(hasResults)) {
        return 'Completed';
      }
      if (this.group.participants.some(hasResults)) {
        return 'In Progress';
      }
      return 'Scheduled';
    },
    getEventStatusClass() {
      const status = this.getEventStatus().toLowerCase();
      if (status === 'completed') return 'completed';
      if (status === 'in progress') return 'in-progress';
      return 'scheduled';
    },
    getParticipantName(participant) {
      if (participant.member && participant.member.profile) {
        return `${participant.member.profile.firstName} ${participant.member.profile.lastName}`;
      }
      if (participant.member) {
        return `${participant.member.firstName || ''} ${participant.member.lastName || ''}`.trim() || participant.member.name || 'Unknown Member';
      }
      return participant.name || 'Unknown';
    },
    getMatchParticipantName(participant) {
      console.log('participant', participant);

      // Handle Custom H2H doubles matches with name1 and name2
      if (participant.name1 && participant.name2) {
        const result = `${participant.name1} / ${participant.name2}`;
        console.log('doubles result:', result);
        return result;
      }

      // Handle Custom H2H singles or incomplete entries with name1
      if (participant.name1) {
        const result = participant.name1 || 'TBD';
        console.log('direct name1 result:', result);
        return result;
      }

      // Check if participant has member and profile data (standard structure)
      if (participant.member && participant.member.profile && participant.member.profile.firstName) {
        const result = `${participant.member.profile.firstName} ${participant.member.profile.lastName || ''}`.trim();
        console.log('member.profile.firstName result:', result);
        return result;
      }

      // Handle single member reference
      if (participant.member) {
        if (participant.member.firstName && participant.member.lastName) {
          const result = `${participant.member.firstName} ${participant.member.lastName}`;
          console.log('member full name result:', result);
          return result;
        }
        const result = participant.member.name || participant.member.name1 || 'Unknown Member';
        console.log('member fallback result:', result);
        return result;
      }

      // Handle members array (for doubles)
      if (participant.members && participant.members.length > 0) {
        const memberNames = participant.members.map(member => {
          if (member.firstName && member.lastName) {
            return `${member.firstName} ${member.lastName}`;
          }
          return member.name || member.name1 || 'Unknown';
        });
        const result = memberNames.join(' / ');
        console.log('members array result:', result);
        return result;
      }

      // Fallback to direct name properties
      const result = participant.name || 'TBD';
      console.log('fallback result:', result);
      return result;
    },
    getRoundHeaders(participants) {
      if (!participants || !participants[0]?.rounds) return [];
      return Object.keys(participants[0].rounds);
    },
    getRoundScores(participant) {
      if (!participant.rounds) return [];
      return Object.values(participant.rounds);
    },
    getTotalStrokes(participant) {
      if (!participant.rounds) return participant.strokes || '-';

      // Calculate total from all completed rounds (strokes > 0)
      let total = 0;
      let hasValidRounds = false;

      Object.values(participant.rounds).forEach((round) => {
        const strokes = parseInt(round.strokes);
        if (strokes && strokes > 0) {
          total += strokes;
          hasValidRounds = true;
        }
      });

      return hasValidRounds ? total : '-';
    },
    getTotalPoints(participant) {
      // First try to sum up stored round points (most accurate)
      if (participant.rounds) {
        let totalPoints = 0;
        let hasStoredPoints = false;

        Object.values(participant.rounds).forEach((round) => {
          // Sum all stored round.points values
          if (round.points !== undefined && round.points !== null && round.points !== '') {
            const points = parseInt(round.points) || 0;
            totalPoints += points;
            hasStoredPoints = true;
          }
        });

        // Return sum of round points if any were found
        if (hasStoredPoints) {
          return totalPoints;
        }
      }

      // Fallback to participant-level points field
      if (participant.points !== undefined && participant.points !== null) {
        return participant.points;
      }

      // If no points data available, show placeholder
      return '-';
    },
    calculateStablefordPoints(strokes, par) {
      const diff = strokes - par;

      // Use configured stableford points if available
      if (this.group.sportConfig?.stablefordPoints) {
        const points = this.group.sportConfig.stablefordPoints;

        // Check for exact match first
        if (points[diff] !== undefined) {
          return parseInt(points[diff]) || 0;
        }

        // Check for "3+" (triple bogey or worse)
        if (diff >= 3 && points['3+'] !== undefined) {
          return parseInt(points['3+']) || 0;
        }
      }

      // Fallback: Standard Stableford scoring system
      if (diff <= -3) return 6; // Albatross or better
      if (diff === -2) return 5; // Eagle
      if (diff === -1) return 4; // Birdie
      if (diff === 0) return 3;  // Par
      if (diff === 1) return 2;  // Bogey
      if (diff === 2) return 1;  // Double Bogey
      return 0; // Triple Bogey or worse
    },
    getRoundScoreClass(strokes, par) {
      if (!strokes || !par) return '';
      const diff = strokes - par;
      if (diff < 0) return 'under-par';
      if (diff > 0) return 'over-par';
      return 'at-par';
    },
    getToParClass(toPar) {
      if (toPar < 0) return 'under-par';
      if (toPar > 0) return 'over-par';
      return 'at-par';
    },
    formatToPar(toPar) {
      if (toPar === 0) return 'E';
      if (toPar > 0) return `+${toPar}`;
      return `${toPar}`;
    },
    getCalculatedToPar(participant) {
      if (!participant.rounds) return 0;

      // Calculate par based only on completed rounds (strokes > 0)
      let totalStrokes = 0;
      let totalPar = 0;
      let completedRounds = 0;

      Object.values(participant.rounds).forEach((round, index) => {
        const strokes = parseInt(round.strokes);

        if (strokes && strokes > 0) { // Only count rounds with valid strokes > 0
          const par = this.getParForRound(index);
          totalStrokes += strokes;
          totalPar += par;
          completedRounds++;
        }
      });

      return completedRounds > 0 ? totalStrokes - totalPar : 0;
    },
    getCompletedRoundsCount(participant) {
      if (!participant.rounds) return 0;

      let completedRounds = 0;
      Object.values(participant.rounds).forEach((round) => {
        const strokes = parseInt(round.strokes);
        if (strokes && strokes > 0) {
          completedRounds++;
        }
      });

      return completedRounds;
    },
    getParForRound(roundIndex) {
      // Use explicit parPerRound configuration if available
      if (this.group.parPerRound && this.group.parPerRound[roundIndex]) {
        return this.group.parPerRound[roundIndex];
      }

      console.log('group', this.group);

      // Use sportConfig parTotal if available (preferred method)
      if (this.group.sportConfig?.parTotal) {
        const holes = this.group.holes || 18;
        const parTotal = this.group.sportConfig.parTotal[holes];
        if (parTotal) {
          return parTotal;
        }
      }

      // Fallback: determine par based on holes configuration
      const holes = this.group.holes || this.group.sportConfig?.holes?.[0] || 18;

      // Standard golf par totals
      if (holes === 9) {
        return 35;
      } else if (holes === 18) {
        return 72;
      } else {
        // For custom hole counts, assume par 4 average
        return holes * 4;
      }
    },
    formatRoundScore(round, roundIndex) {
      const strokes = parseInt(round.strokes);

      // Handle incomplete rounds (0 or no strokes)
      if (!strokes || strokes <= 0) {
        return this.group.scoringType === 'points' ? '0 → 0pts' : '-';
      }

      const par = this.getParForRound(roundIndex);
      const parRelative = strokes - par;

      if (this.group.scoringType === 'points') {
        // Stableford format: "75 → 0pts (E)" - use only stored points
        const points = (round.points !== undefined && round.points !== null && round.points !== '')
          ? parseInt(round.points) || 0
          : 0;
        return `${strokes} → ${points}pts (${this.formatToPar(parRelative)})`;
      } else {
        // Stroke play format: "72 (E)"
        return `${strokes} (${this.formatToPar(parRelative)})`;
      }
    },

    getSetHeaders(match) {
      if (!match.participants || !match.participants[0]?.setScores) return [];
      return match.participants[0].setScores;
    },
    getStatusClass(status) {
      const statusLower = (status || '').toLowerCase();
      if (statusLower.includes('completed') || statusLower.includes('finished')) return 'status-completed';
      if (statusLower.includes('progress') || statusLower.includes('live')) return 'status-live';
      if (statusLower.includes('not started') || statusLower.includes('scheduled')) return 'status-scheduled';
      return 'status-default';
    },
    getSetWinnerClass(participant, opponent, setIndex) {
      const participantScore = parseInt(participant.setScores?.[setIndex]) || 0;
      const opponentScore = parseInt(opponent.setScores?.[setIndex]) || 0;
      if (participantScore > opponentScore) return 'set-winner';
      if (participantScore < opponentScore) return 'set-loser';
      return '';
    },
    getResultText(match) {
      if (match.result?.score) return `Final Score: ${match.result.score}`;
      if (match.result?.sets) return `Sets Won: ${match.result.sets}`;
      const winner = match.participants?.[match.result?.winner];
      if (winner) return `Winner: ${this.getMatchParticipantName(winner)}`;
      return 'Match Completed';
    },
    getParticipantScore(match, index) {
      const participant = match.participants[index];
      const otherParticipant = match.participants[index === 0 ? 1 : 0];

      const setsWon = (participant?.setScores || []).reduce((acc, score, idx) => {
        return acc + (Number(score) > Number(otherParticipant.setScores[idx]) ? 1 : 0);
      }, 0);
      return `(${setsWon})`;
    },
    getSportIcon(type) {
      switch (type) {
      case 'Tennis':
      case 'Badminton':
        return this.tennisballOutline;
      case 'Swimming':
      case 'Athletics':
        return this.bicycleOutline;
      case 'Golf':
        return this.golfOutline;
      case 'Soccer':
      case 'Basketball':
        return this.peopleOutline;
      case 'Gymnastics':
      case 'Diving':
        return this.ribbonOutline;
      default:
        return this.ribbonOutline;
      }
    },
    async getGroup() {
      const groupId = this.$route.params.id;
      try {
        // If this is the specific MongoDB group ID, use the MongoDB data

        // Use the regular API for all groups
        const group = await this.$http.get(`/assemble/api/v1/livescores/result-groups/${groupId}`);
        this.group = group.data.data;

      } catch (error) {
        console.error('Error fetching group:', error);
      }
    },
    formatDateTime(timestamp) {
      return moment(parseInt(timestamp)).format('YYYY-MM-DD HH:mm');
    },
    goBack() {
      this.$router.back();
    },
    getParticipantCount() {
      if (this.isTeamSport) {
        return this.group?.teams?.length || this.group?.matches?.length || 0;
      }
      return this.group?.participants?.length || 0;
    },
    getDisplayParticipants() {
      // Show max 5 participants in the summary
      if (this.isTeamSport) {
        return this.group?.teams?.slice(0, 5) || [];
      }
      return this.group?.participants?.slice(0, 5) || [];
    },
    getAgeGroups() {
      if (this.group?.athleticsTrackConfig?.ageGroups) {
        return this.group.athleticsTrackConfig.ageGroups.join(', ');
      }
      if (this.group?.ageGroups) {
        return this.group.ageGroups.join(', ');
      }
      return 'Open';
    },
    getGenderCategories() {
      if (this.group?.athleticsTrackConfig?.genderCategories) {
        return this.group.athleticsTrackConfig.genderCategories.join(', ');
      }
      if (this.group?.genderCategories) {
        return this.group.genderCategories.join(', ');
      }
      return 'Mixed';
    }
  },
  watch: {
    // Add watcher for route changes
    '$route.params.id': {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.getGroup();
        }
      }
    }
  },
  beforeRouteUpdate(_, __, next) {
    // Reset group data before route update
    this.group = null;
    next();
  },

});
</script>

<style lang="scss" scoped>
// Professional Layout Variables
$primary-color: #1f2937;
$secondary-color: #374151;
$accent-color: #3b82f6;
$success-color: #10b981;
$warning-color: #f59e0b;
$error-color: #ef4444;

$card-bg: #ffffff;
$text-primary: #111827;
$text-secondary: #6b7280;
$text-light: #9ca3af;

$border-radius: 12px;
$shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
$shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);

// Professional Header
.professional-header {
  --background: linear-gradient(135deg, #{$primary-color} 0%, #{$secondary-color} 100%);
  --color: white;
  box-shadow: $shadow-md;

  ion-title {
    font-family: 'BrownStd-Bold', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.025em;
  }

  .back-button {
    padding: 0.5rem;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    ion-icon {
      color: white;
    }
  }
}

// Professional Content
.professional-content {
  --background: #f8fafc;
  padding: 0;
}

.results-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.75rem;
  }

  @media (min-width: 1024px) {
    max-width: 1000px;
  }
}

// Compact Event Header
.event-header-card {
  background: $card-bg;
  border-radius: $border-radius;
  box-shadow: $shadow-md;
  border: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;
  overflow: hidden;
}

.header-main {
  padding: 1.5rem 2rem;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
  }
}

.title-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .event-title {
    font-family: 'BrownStd-Bold', sans-serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: $text-primary;
    margin: 0;
    line-height: 1.2;

    @media (max-width: 768px) {
      font-size: 1.25rem;
    }

    @media (min-width: 1024px) {
      font-size: 1.375rem;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    @media (max-width: 768px) {
      width: 100%;
      justify-content: space-between;
    }
  }

  .status-badge {
    padding: 0.5rem 1rem;
    border-radius: 25px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: nowrap;

    &.completed {
      background: #dcfce7;
      color: #166534;
    }

    &.in-progress {
      background: #fef3c7;
      color: #92400e;
    }

    &.scheduled {
      background: #dbeafe;
      color: #1e40af;
    }
  }

  .details-toggle-btn {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.75rem;
    background: transparent;
    color: $text-secondary;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #f9fafb;
      color: $text-primary;
      border-color: #d1d5db;
    }

    ion-icon {
      font-size: 0.875rem;
      opacity: 0.7;
    }
  }
}

.event-meta {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 1rem;
  }

  .meta-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: $text-secondary;
    font-size: 0.8rem;
    font-weight: 500;

    @media (min-width: 1024px) {
      font-size: 0.75rem;
    }

    ion-icon {
      font-size: 1rem;
      color: $accent-color;

      @media (min-width: 1024px) {
        font-size: 0.9rem;
      }
    }
  }
}

.results-content {
  margin-top: 0;
}

// Details Panel Styling

.details-panel {
  background: $card-bg;
  border-radius: $border-radius;
  box-shadow: $shadow-md;
  border: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;
  overflow: hidden;

  .details-header {
    background: linear-gradient(135deg, $primary-color 0%, $secondary-color 100%);
    padding: 1rem 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
      color: white;
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
    }

    .close-details {
      background: transparent;
      border: none;
      color: white;
      cursor: pointer;
      padding: 0.25rem;
      border-radius: 4px;
      transition: background 0.2s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }

      ion-icon {
        font-size: 1.25rem;
      }
    }
  }

  .details-content {
    padding: 1.5rem;
    max-height: 600px;
    overflow-y: auto;

    @media (max-width: 768px) {
      padding: 1rem;
      max-height: 500px;
    }
  }

  .detail-sections {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

       .detail-section {
       h4 {
         color: $text-primary;
         font-size: 1rem;
         font-weight: 600;
         margin: 0 0 1rem 0;
         padding-bottom: 0.5rem;
         border-bottom: 2px solid #e5e7eb;
       }

       .detail-grid {
         display: grid;
         grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
         gap: 1rem;

         @media (max-width: 768px) {
           grid-template-columns: 1fr;
           gap: 0.75rem;
         }

         .detail-item {
           display: flex;
           flex-direction: column;
           gap: 0.25rem;

           label {
             font-size: 0.75rem;
             font-weight: 600;
             color: $text-secondary;
             text-transform: uppercase;
             letter-spacing: 0.05em;
           }

           span {
             font-size: 0.875rem;
             color: $text-primary;
             font-weight: 500;

             &.status-value {
               &.active {
                 color: $success-color;
               }
               &.inactive {
                 color: $error-color;
               }
             }
           }
         }
       }



         // Sport-specific configurations
     .athletics-config, .golf-config, .team-config, .racquet-config, .boxing-config, .archery-config {
       .config-highlight {
         background: linear-gradient(135deg, #f8fafc 0%, #e5e7eb 100%);
         border-radius: 12px;
         padding: 1.5rem;
         display: grid;
         grid-template-columns: 2fr 1fr 1fr;
         gap: 1.5rem;
         align-items: center;

         @media (max-width: 768px) {
           grid-template-columns: 1fr;
           gap: 1rem;
           padding: 1rem;
         }

         .config-item {
           display: flex;
           flex-direction: column;
           gap: 0.25rem;

           &.main {
             .value-large {
               font-size: 1.25rem;
               font-weight: 700;
               color: $accent-color;
               line-height: 1.2;

               @media (max-width: 768px) {
                 font-size: 1.125rem;
               }

               @media (min-width: 1024px) {
                 font-size: 1.125rem;
               }
             }
           }

           label {
             font-size: 0.75rem;
             font-weight: 600;
             color: $text-secondary;
             text-transform: uppercase;
             letter-spacing: 0.05em;
           }

           span {
             font-size: 0.875rem;
             color: $text-primary;
             font-weight: 600;
           }
         }
       }
     }

     .track-setup {
       display: grid;
       grid-template-columns: repeat(3, 1fr);
       gap: 1rem;

       @media (max-width: 768px) {
         grid-template-columns: repeat(3, 1fr);
         gap: 0.5rem;
       }

       @media (max-width: 480px) {
         gap: 0.25rem;
       }

                .setup-item {
           background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
           border-radius: 8px;
           padding: 1rem;
           text-align: center;
           border: 1px solid #93c5fd;
           min-height: 120px;
           display: flex;
           flex-direction: column;
           justify-content: center;
           align-items: center;

           @media (max-width: 768px) {
             padding: 0.75rem;
             min-height: 100px;
           }

           @media (max-width: 480px) {
             padding: 0.5rem;
             min-height: 90px;
           }

           .setup-icon {
             font-size: 1.5rem;
             margin-bottom: 0.5rem;
             display: block;

             @media (max-width: 768px) {
               font-size: 1.25rem;
               margin-bottom: 0.25rem;
             }

             @media (max-width: 480px) {
               font-size: 1rem;
               margin-bottom: 0.25rem;
             }
           }

           .setup-info {
             display: flex;
             flex-direction: column;
             gap: 0.25rem;
             align-items: center;

             .setup-value {
               font-size: 1.5rem;
               font-weight: 700;
               color: $accent-color;
               line-height: 1;

               @media (max-width: 768px) {
                 font-size: 1.25rem;
               }

               @media (max-width: 480px) {
                 font-size: 1.1rem;
               }
             }

             .setup-label {
               font-size: 0.6rem;
               font-weight: 600;
               color: $text-secondary;
               text-transform: uppercase;
               letter-spacing: 0.05em;
               white-space: nowrap;
               text-align: center;

               @media (max-width: 480px) {
                 font-size: 0.55rem;
                 letter-spacing: 0.02em;
               }
             }
           }
         }
     }



     .event-details {
       display: flex;
       flex-direction: column;
       gap: 0.75rem;

       .detail-row {
         display: flex;
         justify-content: space-between;
         align-items: center;
         padding: 0.75rem 0;
         border-bottom: 1px solid #f3f4f6;

         &:last-child {
           border-bottom: none;
         }

         label {
           font-size: 0.875rem;
           font-weight: 600;
           color: $text-secondary;
         }

         span {
           font-size: 0.875rem;
           color: $text-primary;
           font-weight: 500;
           text-align: right;
         }
       }
     }

     .participants-summary {
       display: flex;
       flex-direction: column;
       gap: 0.5rem;
       max-height: 200px;
       overflow-y: auto;

       .participant-quick {
         display: flex;
         align-items: center;
         gap: 0.75rem;
         padding: 0.75rem;
         background: #f8fafc;
         border: 1px solid #e5e7eb;
         border-radius: 8px;

         .participant-number {
           background: $accent-color;
           color: white;
           padding: 0.25rem 0.5rem;
           border-radius: 4px;
           font-size: 0.75rem;
           font-weight: 600;
           min-width: 2rem;
           text-align: center;
         }

         .participant-name {
           font-weight: 600;
           color: $text-primary;
           flex: 1;
         }

         .participant-meta {
           font-size: 0.75rem;
           color: $text-secondary;
         }
       }
     }
  }

  .pool-filter {
    margin-bottom: 1.5rem;
    padding: 1.25rem 1.5rem;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    border: 1px solid rgba(203, 213, 225, 0.3);
    border-radius: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.06);
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, #3b82f6, #1d4ed8, #7c3aed);
      border-radius: 16px 16px 0 0;
    }

    h4 {
      margin: 0 0 1.25rem 0;
      font-size: 1rem;
      font-weight: 700;
      color: #1e293b;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      letter-spacing: -0.01em;

      &::before {
        content: '🏊‍♂️';
        font-size: 1.1rem;
        filter: none;
      }
    }

    .pool-buttons {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      flex-wrap: wrap;
    }

    /* Minimal overrides for Ionic Chips in this filter */
    .pool-chip {
      --background: var(--ion-color-light, #f1f5f9);
      --color: var(--ion-color-dark, #334155);
      --border-radius: 12px;
      margin: 0;
    }

    @keyframes pulse {
      0%, 100% {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
      }
      50% {
        opacity: 0.7;
        transform: translate(-50%, -50%) scale(1.2);
      }
    }

    @media (max-width: 768px) {
      padding: 1rem 1.25rem;
      margin-bottom: 1.25rem;

      h4 {
        font-size: 0.9rem;
        margin-bottom: 1rem;
      }

      .pool-buttons {
        gap: 0.25rem;

        .pool-btn {
          padding: 0.5rem 1rem;
          font-size: 0.8rem;
          min-width: 44px;
        }
      }
    }

    @media (max-width: 480px) {
      padding: 0.875rem 1rem;

      .pool-buttons {
        gap: 0.25rem;

        .pool-btn {
          padding: 0.4rem 0.8rem;
          font-size: 0.75rem;
          min-width: 40px;
        }
      }
    }
  }
}
</style>